import React from "react";
import { RadialBarChart, RadialBar, PolarAngleAxis } from "recharts";
import { Sparkles } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

const BARS = [
  { key: "hv_liq", v: 78, color: "#2563EB" },
  { key: "hv_sol", v: 71, color: "#3B82F6" },
  { key: "hv_red", v: 88, color: "#22D3EE" },
  { key: "hv_cov", v: 82, color: "#0EA5E9" },
];
const SCALE = ["A", "B", "C", "D"];

export function HeroVisual() {
  const { t } = useI18n();
  const score = 84;
  const gauge = [{ value: score, fill: "#2563EB" }];

  return (
    <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
      {/* glow */}
      <div className="absolute -inset-6 rounded-[2rem] bg-cyan-400/10 blur-3xl" aria-hidden="true" />
      <div className="relative rounded-2xl border border-slate-800 bg-slate-900/90 backdrop-blur-sm shadow-xl shadow-slate-900/5 p-6 sm:p-7"
        data-testid="hero-visual">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-slate-500">{t("hv_rating")}</p>
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-cyan-600 bg-cyan-50 border border-cyan-100 rounded-full px-2 py-0.5">
            <Sparkles className="h-3 w-3" /> AI
          </span>
        </div>

        <div className="mt-4 flex items-center gap-5">
          <div className="relative h-32 w-32 shrink-0">
            <RadialBarChart width={128} height={128} innerRadius="74%" outerRadius="100%" data={gauge} startAngle={90} endAngle={-270}>
              <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
              <RadialBar background={{ fill: "#1E293B" }} dataKey="value" cornerRadius={16} />
            </RadialBarChart>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-mono-fin text-3xl font-bold text-white tracking-tight">{score}</span>
              <span className="text-[9px] uppercase tracking-widest text-slate-500">score</span>
            </div>
          </div>
          <div>
            <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-blue-800 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/20">
              <span className="font-heading text-3xl font-extrabold text-white">A</span>
            </div>
            <p className="mt-2 text-sm font-semibold text-slate-100">{t("hv_grade_band")}</p>
            <p className="text-xs text-slate-400">{t("hv_pd")}: &lt; 0,5%</p>
          </div>
        </div>

        {/* mini ratio bars */}
        <div className="mt-6 space-y-2.5">
          {BARS.map((b, i) => (
            <div key={b.key} className="flex items-center gap-3">
              <span className="w-20 text-[11px] text-slate-400 shrink-0">{t(b.key)}</span>
              <div className="flex-1 h-2 rounded-full bg-slate-800 overflow-hidden">
                <div className="h-full rounded-full animate-fade-up"
                  style={{ width: `${b.v}%`, backgroundColor: b.color, animationDelay: `${200 + i * 90}ms` }} />
              </div>
              <span className="w-8 text-right font-mono-fin text-[11px] text-slate-300">{b.v}</span>
            </div>
          ))}
        </div>

        {/* A/B/C/D scale */}
        <div className="mt-6 flex items-center gap-1.5">
          {SCALE.map((g) => (
            <div key={g} className={`flex-1 rounded-md py-1.5 text-center text-xs font-bold font-heading transition-colors
              ${g === "A" ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-500"}`}>
              {g}
            </div>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-center gap-1.5 text-[10px] text-slate-500">
          <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse" />
          {t("hv_mc")}
        </div>
      </div>
    </div>
  );
}
